import { Injectable } from '@angular/core';
import API, { graphqlOperation } from '@aws-amplify/api';
import { CompanyQuery, CompaniesQuery, CompanyFilterInput } from 'app/api/API.service';
import { map } from 'rxjs/operators';
import { Observable, from } from 'rxjs';

export interface AccountingYear {
  startDate: string;
  endDate?: string;
  closedAccounting?: boolean;
}

export interface CompanySubset {
  id: string;
  name: string;
  untilDateExcluded: string | null;
  accountingYears?: AccountingYear[] | null; 
  lastFileUpload: string | null;
  lastAccountingFileUploadSuccess: string | null;
  lastBalanceFileUploadSuccess: string | null;
  lastParametersFileUploadSuccess: string | null;
  lastAccountingFileUploadSuccessDate: string | null;
}

export interface CompanyConnection {
  items: CompanySubset[];
  lastEvaluatedKey: any;
}

export interface Company {
  id: string;
  name: string;
  untilDateExcluded: string | null;
  template10?: string | null;
  accountingYears?: AccountingYear[] | null; 
  importOptions?: Map<string, ImportOption> | null;
  lastFileUpload: string | null;
  lastAccountingFileUploadSuccess: string | null;
  lastBalanceFileUploadSuccess: string | null;
  lastParametersFileUploadSuccess: string | null;
  lastAccountingFileUploadSuccessDate: string | null;
}

class CompanyRaw {
  id: string;
  name: string;
  untilDateExcluded: string | null;
  template10: string | null;
  accountingYears?: AccountingYear[] | null; 
  importOptions: string | null;
  exportOptions: string | null;
  lastFileUpload: string | null;
  lastAccountingFileUploadSuccess: string | null;
  lastBalanceFileUploadSuccess: string | null;
  lastParametersFileUploadSuccess: string | null;
  lastAccountingFileUploadSuccessDate: string | null;
}

export interface ImportOption {
  fileType: number;
  sheetName: string;
  parser: Parser;
  columnsMap: Map<string, string>;
}

export interface Parser {
  dateLayout: string;
  decimalSep: string;
  currencyPattern: string;
  auxiliaries: Map<string, string>;
  labelWithDocument: boolean;
}

export interface DeleteCompanyDataEntry {
  id: string;
  dataType: string;
}

@Injectable({
  providedIn: 'root'
})
export class CompanyAPIService {
  
  /**
   * Get a list of CompanySubset
   *
   * @param {CompanyFilterInput} [filter]
   * @param {number} [limit]
   * @param {string} [exclusiveStartKey]
   * @returns {Promise<CompanyConnection>}
   * @memberof CompanyAPIService
   */
  Companies(
    filter?: CompanyFilterInput,
    limit?: number,
    exclusiveStartKey?: string
  ): Observable<CompanyConnection> {
    const statement = `query Companies($filter: CompanyFilterInput, $limit: Int, $exclusiveStartKey: AWSJSON) {
        companies(filter: $filter, limit: $limit, exclusiveStartKey: $exclusiveStartKey) {
          items {
            id
            name
            untilDateExcluded
            accountingYears { startDate, endDate, closedAccounting }
            lastFileUpload
            lastAccountingFileUploadSuccess
            lastBalanceFileUploadSuccess
            lastParametersFileUploadSuccess
            lastAccountingFileUploadSuccessDate
          
          }
          lastEvaluatedKey
        }
      }`;
    const gqlAPIServiceArguments: any = {};
    if (filter) {
      gqlAPIServiceArguments.filter = filter;
    }
    if (limit) {
      gqlAPIServiceArguments.limit = limit;
    }
    if (exclusiveStartKey) {
      gqlAPIServiceArguments.exclusiveStartKey = exclusiveStartKey;
    }
    return from(API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )).pipe(
      map((response: any): CompanyConnection => {
        return response.data.companies;
      })
    );
  }

  /**
   * Get the detail of a company
   *
   * @param {string} id
   * @returns {Promise<Company>}
   * @memberof CompanyAPIService
   */
  Company(id: string): Observable<Company> {
    const statement = `query Company($id: String!) {
        company(id: $id) {
          id
          name
          untilDateExcluded
          template10
          accountingYears { startDate, endDate, closedAccounting }
          importOptions
          lastFileUpload
          lastAccountingFileUploadSuccess
          lastBalanceFileUploadSuccess
          lastParametersFileUploadSuccess
          lastAccountingFileUploadSuccessDate
        }
      }`;
    const gqlAPIServiceArguments: any = {
      id
    };
    return from(API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )).pipe(
      map((response: any) => {
        return this.toCompany(response.data.company as CompanyRaw);
      })
    );
  }

  // Parse AppSync format to object
  private toCompany(c: CompanyRaw): Company {
    const company: Company = {
      id: c.id,
      name: c.name,
      untilDateExcluded: c.untilDateExcluded,
      template10: c.template10,
      accountingYears: c.accountingYears,
      importOptions: null,
      lastFileUpload: c.lastFileUpload,
      lastAccountingFileUploadSuccess: c.lastAccountingFileUploadSuccess,
      lastBalanceFileUploadSuccess: c.lastBalanceFileUploadSuccess,
      lastParametersFileUploadSuccess: c.lastParametersFileUploadSuccess,
      lastAccountingFileUploadSuccessDate: c.lastAccountingFileUploadSuccessDate

    };
    if (c.importOptions !== null) {
      company.importOptions = JSON.parse(c.importOptions);
    }
    return company;
  }

  // Parse object to AppSync format
  private toCompanyRaw(c: Company): CompanyRaw {
    const company = new CompanyRaw();
    company.id = c.id;
    company.name = c.name;
    company.untilDateExcluded = c.untilDateExcluded;
    company.template10 = c.template10;
    company.accountingYears = c.accountingYears;    
    company.importOptions = null;
    if (c.importOptions !== null) {
      company.importOptions = JSON.stringify(c.importOptions);
    }
    company.lastFileUpload = c.lastFileUpload;
    company.lastAccountingFileUploadSuccess = c.lastAccountingFileUploadSuccess;
    company.lastBalanceFileUploadSuccess = c.lastBalanceFileUploadSuccess;
    company.lastParametersFileUploadSuccess = c.lastParametersFileUploadSuccess;
    company.lastAccountingFileUploadSuccessDate = c.lastAccountingFileUploadSuccessDate;
    return company;
  }

    DeleteCompanyData(
        input: { dataType: string; id: string }
    ): Observable<DeleteCompanyDataEntry> {
    const statement = `mutation DeleteCompanyData($input: DeleteCompanyDataInput!) {
      deleteCompanyData(input: $input) {
        id
      }
    }`;
    const gqlAPIServiceArguments: any = {
      input
    };

    return from(API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments),
    )).pipe(
      map((response: any): DeleteCompanyDataEntry => {
        return response.data.deleteCompanyData;
      })
    );
  }
}
